import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, CheckCircle2, BarChart3 } from 'lucide-react';
import { investmentHighlights, investmentTiers, projects } from '../data/mock';
import { ParticlesBg } from '../components/layout/ParticlesBg';
import { Section, SectionHeading, Eyebrow, PageHero, Ridgeline, fadeInUp } from '../components/layout/Section';
import { SpotlightCard, CountUp, WindowFrame, StatusPill, Reveal } from '../components/layout/Tech';
import { Globe3D, ScrollTilt } from '../components/layout/Motion3D';
import { ChartCard, Donut, BarsH, TierMatrix } from '../components/layout/Charts';

const byCategory = Object.entries(projects.reduce((m, p) => ({ ...m, [p.category]: (m[p.category] || 0) + 1 }), {})).map(([name, value]) => ({ name, value }));
const byStatus = Object.entries(projects.reduce((m, p) => ({ ...m, [p.status]: (m[p.status] || 0) + 1 }), {})).map(([name, value]) => ({ name, value }));
const costIndex = [{ name: 'Western-market rate', value: 100 }, { name: 'Globistaan rate', value: 40 }];

const highlightAccents = ['accent-orange', 'accent-green', 'accent-purple', 'accent-cyan'];
const tierAccents = ['accent-blue', 'accent-cyan', 'accent-purple'];

export default function Investors() {
  return (
    <main>
      <PageHero
        tone="navy"
        eyebrow="Investment Opportunities"
        title="Invest in the AI Future"
        subtitle="Globistaan is building a portfolio of AI-powered products across healthcare, education, and commerce — from the serene mountains of Himachal Pradesh."
        maxWidth={950}
        glows={[
          { color: 'rgba(34,211,238,0.26)', top: '-10%', left: '-6%', size: 600 },
          { color: 'rgba(124,58,237,0.3)', bottom: '-20%', right: '-6%', size: 600 }
        ]}
        ridge
        decor={<Globe3D size={600} className="hero-globe" accent="#22D3EE" accent2="#A78BFA" />}
      >
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }} className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <Link to="/contact" className="btn-primary btn-accent" style={{ padding: '1rem 2.4rem', fontSize: '0.85rem' }}>
            Request Investor Deck <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="#tiers" className="btn-secondary" style={{ padding: '1rem 2.4rem', fontSize: '0.85rem' }}>
            View Tiers <ArrowUpRight className="w-4 h-4" />
          </a>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {investmentHighlights.map((h) => (
            <div key={h.title}>
              <CountUp value={h.metric} style={{ fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', color: '#67E8F9' }} />
              <p className="kicker mt-1">{h.metricLabel}</p>
            </div>
          ))}
        </motion.div>
      </PageHero>

      <Section tone="sky" pattern="grid" glows={[{ color: 'rgba(96,165,250,0.35)', top: '-20%', right: '-8%', size: 560 }]}>
        <SectionHeading eyebrow="Why Invest" title="Investment Highlights" className="mb-16" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {investmentHighlights.map((item, i) => {
            const Icon = item.icon;
            return (
              <Reveal key={item.title} delay={i * 0.08} className="h-full">
                <SpotlightCard accent={highlightAccents[i % highlightAccents.length]} className="h-full" style={{ '--card-pad': '2.25rem' }}>
                  <div className="flex items-start gap-5">
                    <div className="icon-well lg"><Icon className="w-7 h-7" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-3 mb-3 flex-wrap">
                        <CountUp value={item.metric} style={{ fontSize: '2.4rem' }} />
                        <span className="kicker">{item.metricLabel}</span>
                      </div>
                      <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>{item.title}</h3>
                      <p style={{ fontSize: '0.92rem', lineHeight: 1.6, color: 'var(--text-secondary)' }}>{item.description}</p>
                    </div>
                  </div>
                </SpotlightCard>
              </Reveal>
            );
          })}
        </div>
      </Section>

      <Section tone="slate" pattern="grid" topo padding="py-20">
        <SectionHeading eyebrow="At a glance" title="The portfolio in numbers" subtitle="Every figure below is derived from the projects, clients and registrations listed on this site." className="mb-12" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          <Reveal className="h-full"><ChartCard title="Portfolio by category" caption="Products and client builds, grouped by offering." data={byCategory}><Donut data={byCategory} centerLabel="builds" size={170} /></ChartCard></Reveal>
          <Reveal delay={0.06} className="h-full"><ChartCard title="Portfolio by status" caption="Live, demo and in-development work." data={byStatus}><Donut data={byStatus} centerLabel="projects" size={170} /></ChartCard></Reveal>
          <Reveal delay={0.12} className="h-full"><ChartCard title="Cost advantage" caption="Indexed engineering rate: Western markets = 100. Reflects the 60% saving stated above." data={costIndex} valueLabel="Index"><BarsH data={costIndex} categorical /></ChartCard></Reveal>
        </div>
      </Section>

      <Section
        tone="indigo"
        pattern="dots"
        aurora
        glows={[
          { color: 'rgba(124,58,237,0.3)', top: '-10%', left: '-6%', size: 600 },
          { color: 'rgba(34,211,238,0.18)', bottom: '-10%', right: '-6%', size: 520 }
        ]}
      >
        <SectionHeading eyebrow="Portfolio" title="Product Portfolio" className="mb-16" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((project, i) => {
            const Icon = project.icon;
            return (
              <Reveal key={project.id} delay={Math.min(i, 3) * 0.06} className="h-full">
                <SpotlightCard accent={project.accentClass} className="h-full" style={{ '--card-pad': '1.75rem' }}>
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="icon-well sm"><Icon className="w-5 h-5" /></div>
                    <StatusPill status={project.status} />
                  </div>
                  <span className="chip mb-3" style={{ color: 'var(--card-accent)', borderColor: 'var(--card-accent)', textTransform: 'uppercase', fontSize: '0.62rem' }}>{project.category}</span>
                  <h4 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>{project.title}</h4>
                  <p style={{ fontSize: '0.86rem', lineHeight: 1.6, color: 'var(--text-secondary)' }}>{project.description}</p>
                </SpotlightCard>
              </Reveal>
            );
          })}
        </div>
      </Section>

      <Section
        id="tiers"
        tone="slate"
        pattern="grid"
        glows={[
          { color: 'rgba(34,211,238,0.2)', top: '-20%', left: '-6%', size: 560 },
          { color: 'rgba(251,191,36,0.14)', bottom: '-20%', right: '-6%', size: 560 }
        ]}
        contentWidth={1100}
        className="scroll-mt-20"
      >
        <SectionHeading eyebrow="Investment Tiers" title="Partnership Opportunities" className="mb-16" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {investmentTiers.map((tier, i) => (
            <Reveal key={tier.tier} delay={i * 0.08} className="h-full" style={{ position: 'relative' }}>
              {tier.highlighted && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-20">
                  <Eyebrow style={{ background: '#22D3EE', color: '#06202A', borderColor: '#22D3EE' }}>Recommended</Eyebrow>
                </div>
              )}
              <SpotlightCard
                accent={tierAccents[i % tierAccents.length]}
                className="h-full"
                style={{
                  '--card-pad': '2.5rem',
                  transform: tier.highlighted ? 'scale(1.02)' : undefined,
                  boxShadow: tier.highlighted ? '0 0 0 1px rgba(34,211,238,0.5), 0 0 60px rgba(34,211,238,0.15), 0 30px 70px rgba(0,0,0,0.5)' : undefined
                }}
              >
                <div className="flex flex-col h-full">
                  <h3 className="mb-1" style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-primary)' }}>{tier.tier}</h3>
                  <p className="mb-1" style={{ fontSize: '1.15rem', fontWeight: 500, color: 'var(--text-primary)' }}>{tier.range}</p>
                  <p className="kicker mb-6">Structure: {tier.equity}</p>
                  <div className="flex flex-col gap-3">
                    {tier.benefits.map((b) => (
                      <span key={b} className="flex items-center gap-2.5" style={{ fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--card-accent)' }} />{b}
                      </span>
                    ))}
                  </div>
                  <Link
                    to="/contact"
                    className={tier.highlighted ? 'btn-primary btn-accent' : 'btn-secondary'}
                    style={{ textDecoration: 'none', width: '100%', marginTop: 'auto', paddingTop: '0.85rem', paddingBottom: '0.85rem', fontSize: '0.8rem' }}
                  >
                    Inquire Now
                  </Link>
                </div>
              </SpotlightCard>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <ChartCard title="Compare the tiers" caption="Every benefit across all three partnership structures.">
            <TierMatrix tiers={investmentTiers} />
          </ChartCard>
        </Reveal>
      </Section>

      <Section tone="sand" pattern="hatch" glows={[{ color: 'rgba(251,191,36,0.3)', top: '-30%', left: '-8%', size: 560 }]} contentWidth={1000} padding="py-20">
        <motion.div {...fadeInUp}>
          <ScrollTilt>
          <WindowFrame url="globistaan.com / investors / financials" right={<StatusPill status="In Development" />}>
            <div className="readout" style={{ padding: '2.5rem', textAlign: 'center' }}>
              <div className="icon-well lg mx-auto mb-5" style={{ '--card-accent': '#FBBF24', background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.1)' }}><BarChart3 className="w-6 h-6" /></div>
              <h3 className="mb-3" style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff', fontFamily: 'var(--font-display)' }}>Financial Data Coming Soon</h3>
              <p className="mx-auto mb-6" style={{ maxWidth: '460px', fontSize: '0.86rem', lineHeight: 1.7, color: 'rgba(255,255,255,0.7)' }}>
                [Placeholder for financial projections, revenue models, burn rate, and growth metrics. Contact us for the complete investor deck.]
              </p>
              <Link to="/contact" className="btn-primary btn-accent text-xs" style={{ textDecoration: 'none', padding: '0.75rem 1.5rem' }}>
                Request Full Deck <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </WindowFrame>
          </ScrollTilt>
        </motion.div>
      </Section>

      <Section
        tone="plum"
        rule={false}
        glows={[
          { color: 'rgba(167,139,250,0.3)', top: '-30%', left: '15%', size: 640 },
          { color: 'rgba(34,211,238,0.2)', bottom: '-30%', right: '10%', size: 560 }
        ]}
        contentWidth={720}
        padding="py-28"
      >
        <ParticlesBg />
        <motion.div {...fadeInUp} className="text-center relative z-10">
          <h2 className="h-section mb-5" style={{ fontSize: 'clamp(1.7rem, 3.8vw, 2.5rem)' }}>Ready to Partner with Us?</h2>
          <p className="lede mx-auto mb-10">Schedule a call with our founding team to discuss investment opportunities.</p>
          <Link to="/contact" className="btn-primary btn-accent" style={{ padding: '1rem 2.5rem', fontSize: '0.85rem' }}>
            Schedule a Call <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
        <Ridgeline opacity={0.7} height={140} />
      </Section>
    </main>
  );
}
